import { AxiosError } from 'axios';
import { ShieldAlert } from 'lucide-react';
import { useState } from 'react';

import { authService } from '../../services/auth';
import { generateErrorMessages } from '../../utils/showApiErrors';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';

interface AlreadyLoggedInProps {
  description: string;
  title?: string;
  actionLabel?: string;
}

/**
 * Shown when a signed-in visitor opens a link meant for an anonymous session —
 * an account-setup or password-reset link from email.
 *
 * Those pages used to sit behind `UnauthenticatedRoute`, which bounced the
 * visitor to `/` and silently dropped the `?token=`, leaving them with no idea
 * why their emailed link did nothing. Logging out here flips `authState`; the
 * page re-renders and picks up the token that is still in the URL, so the
 * visitor lands straight on the form instead of having to re-open the email.
 */
export const AlreadyLoggedIn = ({
  description,
  title = "You're already signed in",
  actionLabel = 'Log out and continue'
}: AlreadyLoggedInProps): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | string[] | null>(null);

  const onLogout = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await authService.logout();
      // Deliberately no `setLoading(false)` and no redirect on success: the
      // logout flips `authState`, the parent page swaps this screen out for the
      // real flow, and touching state on the unmounted component would warn.
    } catch (e) {
      setError(generateErrorMessages(e as AxiosError));
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-[18px]">
      <div className="flex flex-col gap-[11px]">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-gold-50 text-brand-gold">
          <ShieldAlert className="h-7 w-7" aria-hidden="true" />
        </span>
        <h1 className="font-onboarding-1 text-[2rem] font-bold leading-[1.04] tracking-[-0.025em] text-primary min-[880px]:text-[2.35rem]">
          {title}
        </h1>
        <p className="text-[15.5px] leading-[1.5] text-[var(--ink-2)]">{description}</p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {Array.isArray(error) ? (
              <div className="space-y-1">
                {error.map((err, index) => (
                  <div key={index}>{err}</div>
                ))}
              </div>
            ) : (
              error
            )}
          </AlertDescription>
        </Alert>
      )}

      <button
        type="button"
        onClick={() => void onLogout()}
        disabled={loading}
        className="btn-cta-orange mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-lg text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? (
          <>
            <Spinner size="sm" className="border-white" />
            <span>Logging out...</span>
          </>
        ) : (
          actionLabel
        )}
      </button>
    </div>
  );
};
